import { auth } from '../firebase/config';
import type { Request, Shift } from '../models/data';

const importMetaEnv = (import.meta as any).env || {};

const BACKEND_BASE_URL =
  importMetaEnv.VITE_BACKEND_BASE_URL ||
  importMetaEnv.VITE_FUNCTIONS_BASE_URL ||
  'https://mintleaf-foglalasok-4-ai-copy.onrender.com';

export const DASHBOARD_READ_UNIT_CHUNK_SIZE = 10;

type DashboardReadErrorDetails = {
  path: string;
  statusCode?: number;
  responseBody?: unknown;
  parseError?: string;
};

export type DashboardReadDiagnosticStage =
  | 'request-started'
  | 'backend-response-received'
  | 'response-parsed'
  | 'response-mapped';

export type DashboardReadDiagnosticEvent = {
  operation: 'listDashboardShifts' | 'listDashboardRequests';
  path: string;
  stage: DashboardReadDiagnosticStage;
  durationMs: number;
  statusCode?: number;
  payloadCount?: number;
  mode?: 'window' | 'full';
  fromDateKey?: string;
  toDateKey?: string;
};

export type DashboardReadDiagnostics = (event: DashboardReadDiagnosticEvent) => void;

const readNow = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const getAuthToken = async (): Promise<string> => {
  const sessionToken = window.localStorage.getItem('mintleaf_api_token');
  if (sessionToken) return sessionToken;

  const user = auth.currentUser;
  if (!user) throw new Error('UNAUTHENTICATED');
  return user.getIdToken();
};

const createDashboardReadError = (
  message: string,
  details: DashboardReadErrorDetails
): Error => {
  const error = new Error(message);
  (error as any).statusCode = details.statusCode;
  (error as any).path = details.path;
  (error as any).responseBody = details.responseBody;
  (error as any).details = details;
  return error;
};

const parseResponseBody = async (
  response: Response,
  path: string
): Promise<unknown> => {
  const rawBody = await response.text();
  if (!rawBody) return null;

  try {
    return JSON.parse(rawBody);
  } catch (error) {
    throw createDashboardReadError('DASHBOARD_READ_INVALID_JSON', {
      path,
      statusCode: response.status,
      responseBody: rawBody,
      parseError: error instanceof Error ? error.message : String(error),
    });
  }
};

const request = async <T>(path: string, signal?: AbortSignal, diagnostics?: DashboardReadDiagnostics, operation: DashboardReadDiagnosticEvent['operation'] = 'listDashboardShifts', metadata?: Pick<DashboardReadDiagnosticEvent, 'mode' | 'fromDateKey' | 'toDateKey'>): Promise<T> => {
  const token = await getAuthToken();
  const startedAt = readNow();
  diagnostics?.({ operation, path, stage: 'request-started', durationMs: 0, ...metadata });
  const response = await fetch(`${BACKEND_BASE_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    signal,
  });

  const responseReceivedAt = readNow();
  diagnostics?.({
    operation,
    path,
    stage: 'backend-response-received',
    durationMs: responseReceivedAt - startedAt,
    statusCode: response.status,
    ...metadata,
  });

  const payload = await parseResponseBody(response, path);
  diagnostics?.({
    operation,
    path,
    stage: 'response-parsed',
    durationMs: readNow() - responseReceivedAt,
    statusCode: response.status,
    payloadCount: Array.isArray(payload) ? payload.length : undefined,
    ...metadata,
  });

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'message' in payload
        ? String((payload as { message?: unknown }).message || 'DASHBOARD_READ_API_ERROR')
        : 'DASHBOARD_READ_API_ERROR';
    throw createDashboardReadError(message, {
      path,
      statusCode: response.status,
      responseBody: payload,
    });
  }

  return payload as T;
};

const encodeUnitIds = (unitIds: string[]): string =>
  encodeURIComponent(Array.from(new Set(unitIds.filter(Boolean))).join(','));

export type DashboardShiftReadOptions = {
  fromDateKey?: string;
  toDateKey?: string;
  mode?: 'window' | 'full';
};

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const normalizeShiftReadOptions = (options?: DashboardShiftReadOptions): DashboardShiftReadOptions => {
  if (!options || options.mode === 'full') return { mode: 'full' };
  const fromDateKey = options.fromDateKey?.trim();
  const toDateKey = options.toDateKey?.trim();
  if (options.mode === 'window' && fromDateKey && toDateKey && DATE_KEY_PATTERN.test(fromDateKey) && DATE_KEY_PATTERN.test(toDateKey) && fromDateKey <= toDateKey) {
    return { mode: 'window', fromDateKey, toDateKey };
  }
  return { mode: 'full' };
};

const buildShiftPath = (chunk: string[], options?: DashboardShiftReadOptions): string => {
  const normalizedOptions = normalizeShiftReadOptions(options);
  const params = new URLSearchParams();
  if (normalizedOptions.mode === 'window' && normalizedOptions.fromDateKey && normalizedOptions.toDateKey) {
    params.set('from', normalizedOptions.fromDateKey);
    params.set('to', normalizedOptions.toDateKey);
  }
  const windowQuery = params.toString();
  return `/api/shifts?unitIds=${encodeUnitIds(chunk)}${windowQuery ? `&${windowQuery}` : ''}`;
};

const normalizeUnitIds = (unitIds: string[]): string[] =>
  Array.from(new Set(unitIds.map(unitId => unitId.trim()).filter(Boolean)));

const toDashboardShiftDate = (value: unknown): unknown => {
  if (typeof value === 'undefined' || value === null) return value;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  if (typeof value === 'string') return value;
  if (typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
  if (typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
    const resolved = (value as { toDate: () => unknown }).toDate();
    return resolved instanceof Date && !Number.isNaN(resolved.getTime())
      ? resolved.toISOString()
      : null;
  }
  if (typeof value === 'object') {
    const timestamp = value as { seconds?: unknown; nanoseconds?: unknown; _seconds?: unknown; _nanoseconds?: unknown };
    const seconds = typeof timestamp.seconds === 'number' ? timestamp.seconds : timestamp._seconds;
    const nanoseconds = typeof timestamp.nanoseconds === 'number' ? timestamp.nanoseconds : timestamp._nanoseconds;
    if (typeof seconds === 'number') {
      const millis = seconds * 1000 + (typeof nanoseconds === 'number' ? Math.floor(nanoseconds / 1_000_000) : 0);
      const parsed = new Date(millis);
      return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
    }
  }
  return null;
};

const normalizeDashboardShift = (shift: unknown): Shift => {
  const value = shift && typeof shift === 'object' ? shift as Record<string, unknown> : {};
  return {
    ...value,
    start: toDashboardShiftDate(value.start) as Shift['start'],
    end: toDashboardShiftDate(value.end) as Shift['end'],
  } as Shift;
};

const assertArrayPayload = <T>(payload: unknown, path: string): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  throw createDashboardReadError('DASHBOARD_READ_EXPECTED_ARRAY', {
    path,
    responseBody: payload,
  });
};

export const listDashboardShifts = async (unitIds: string[], signal?: AbortSignal, diagnostics?: DashboardReadDiagnostics, options?: DashboardShiftReadOptions): Promise<Shift[]> => {
  const normalized = normalizeUnitIds(unitIds);
  if (normalized.length === 0) return [];

  const chunks: string[][] = [];
  for (let index = 0; index < normalized.length; index += DASHBOARD_READ_UNIT_CHUNK_SIZE) {
    chunks.push(normalized.slice(index, index + DASHBOARD_READ_UNIT_CHUNK_SIZE));
  }

  const results = await Promise.all(
    chunks.map(async chunk => {
      const path = buildShiftPath(chunk, options);
      const normalizedOptions = normalizeShiftReadOptions(options);
      const payload = await request<unknown>(path, signal, diagnostics, 'listDashboardShifts', normalizedOptions);
      const rawShifts = assertArrayPayload<unknown>(payload, path);
      const mapStartedAt = readNow();
      const mapped = rawShifts.map(normalizeDashboardShift);
      diagnostics?.({
        operation: 'listDashboardShifts',
        path,
        stage: 'response-mapped',
        durationMs: readNow() - mapStartedAt,
        payloadCount: mapped.length,
        mode: normalizedOptions.mode,
        fromDateKey: normalizedOptions.fromDateKey,
        toDateKey: normalizedOptions.toDateKey,
      });
      return mapped;
    })
  );
  return results.flat();
};

export const listDashboardRequests = async (unitIds: string[]): Promise<Request[]> => {
  const normalized = normalizeUnitIds(unitIds);
  if (normalized.length === 0) return [];

  const chunks: string[][] = [];
  for (let index = 0; index < normalized.length; index += DASHBOARD_READ_UNIT_CHUNK_SIZE) {
    chunks.push(normalized.slice(index, index + DASHBOARD_READ_UNIT_CHUNK_SIZE));
  }

  const results = await Promise.all(
    chunks.map(async chunk => {
      const path = `/api/leave-requests?unitIds=${encodeUnitIds(chunk)}`;
      return assertArrayPayload<Request>(await request<unknown>(path, undefined, undefined, 'listDashboardRequests'), path);
    })
  );
  return results.flat();
};
